import { Check, TriangleAlert } from "lucide-react";

/**
 * What the page knows about compiling THIS submission (flowstack#1398).
 *
 * `idle` is the page as it has always been: nothing has been compiled, so the
 * line states the work rather than a verdict. The other three are what a dry
 * run buys — and they are about the exact body that was sent, which is why the
 * caller resets this to `idle` the moment an input or a parameter changes. A
 * verdict about a submission you have since edited is worse than no verdict.
 */
export type CompileVerdict =
  | { kind: "idle" }
  | { kind: "pending" }
  | { kind: "compiled"; nodeCount: number; fanoutNodeCount: number; gatedCount: number }
  | { kind: "failed"; message: string };

interface SubmitReadinessProps {
  /** Declared flow inputs, or `undefined` when the manifest did not load and
   *  the page therefore does not know what the flow declares. */
  declaredInputCount: number | undefined;
  /** Declared input names with no artifact selected. Empty ⇒ all resolved. */
  missingInputs: readonly string[];
  /** Nodes the flow template declares, from the DAG endpoint. `undefined`
   *  while the DAG is loading or after it errored. */
  nodeCount: number | undefined;
  /** What a dry run has established about this exact body, if anything. */
  verdict?: CompileVerdict;
}

/**
 * The pre-flight readiness line beside Submit — the 2026-08-21 canvas's
 * `Inputs resolve · 15 tiles compile` (flowstack#1282).
 *
 * The canvas's thesis for this page is *pre-submit confidence*: before you
 * commit to creating 15+ billed runs, the page should say what it already
 * knows about the submission you are about to make. The stub-mode banner
 * (#1174) answers "will this reach real compute". This answers the two
 * smaller questions above the Submit button: **is every input wired**, and
 * **how much is about to be compiled**.
 *
 * Two deliberate departures from the canvas's copy:
 *
 * 1. **"nodes", not "tiles".** The number is the flow template's node count —
 *    the same figure `FlowStructurePanel` prints as `N nodes · M edges` and
 *    the same one `POST /api/flows/submit` returns as `node_count`. The
 *    canvas wrote "tiles"; adopting it here would give one page two words
 *    for one quantity, which costs more than matching the artboard's noun.
 * 2. **"to compile" until something has compiled it.** When #1282 wrote this
 *    line there was no compile-only endpoint to ask, so `15 tiles compile`
 *    would have asserted a result we had not obtained — the same over-claim
 *    Rupert struck from the execution-mode notice in PR-1175 r0. There is one
 *    now: `POST /api/flows/dry-run` (flowstack#1398) runs the submission's
 *    whole synchronous half and writes nothing. So the tense moves with the
 *    evidence. Idle, the line still states the work — **`15 nodes to
 *    compile`**. After a dry run of *this* body it states the result —
 *    **`15 nodes compile`** — and after a failed one it says so and names the
 *    reason. The clause never claims more than has actually happened, which
 *    was #1282's rule and is unchanged; what changed is that something can now
 *    happen.
 *
 * The verdict is about the body that was sent. The page resets it to `idle`
 * on any change to the inputs or parameters, because a green line describing
 * a submission the reader has since edited is worse than a line that admits
 * it does not know.
 *
 * "Inputs resolve" IS asserted, because the client genuinely establishes it:
 * every declared input has a selection, and `ArtifactInputPicker` only offers
 * catalog artifacts whose `typed_object` equals the port's declared type — so
 * a full selection satisfies the name, presence and type checks
 * `_resolve_flow_inputs` performs server-side. It does NOT cover that route's
 * last check — a `scientific_status="failed"` upstream, which 400s without an
 * explicit `override_failed_upstream` and which the Flow submit surface does
 * not yet expose at all (only the Tile page renders `FailedUpstreamBanner`).
 * Hence the word is "resolve", not "accepted": the inputs are wired and typed,
 * which is a smaller claim than the submission being accepted.
 *
 * Renders nothing at all when it knows neither fact — an empty line is
 * better than a line that says nothing.
 */
export function SubmitReadiness({
  declaredInputCount,
  missingInputs,
  nodeCount,
  verdict = { kind: "idle" },
}: SubmitReadinessProps): JSX.Element | null {
  const inputsResolve = declaredInputCount !== undefined && missingInputs.length === 0;

  const inputClause =
    declaredInputCount === undefined
      ? undefined
      : missingInputs.length > 0
        ? `Select an artifact for: ${missingInputs.join(", ")}`
        : declaredInputCount === 0
          ? "No inputs to resolve"
          : "Inputs resolve";

  const nodeClause = compileClause(verdict, nodeCount);

  const clauses = [inputClause, nodeClause].filter((c): c is string => c !== undefined);
  if (clauses.length === 0) return null;

  return (
    <p
      // A status region rather than plain text: the line changes as inputs are
      // wired, and the change is the point. Polite, so it never interrupts.
      role="status"
      data-testid="submit-readiness"
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
    >
      {/* The tick marks "every input is wired", not "this submission will
          succeed" — so it is muted chrome, not a success colour. A failed
          compile is the one state that earns a different glyph: it is the only
          one saying the submission would be REJECTED, rather than reporting
          what is or is not yet known. */}
      {verdict.kind === "failed" ? (
        <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-state-failed-validation" aria-hidden />
      ) : (
        inputsResolve && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
      )}
      <span>{clauses.join(" · ")}</span>
    </p>
  );
}

/**
 * The node clause, in whichever tense the evidence supports.
 *
 * `nodeCount` (the template's declared size, from the DAG endpoint) is what
 * the idle line counts. A compiled verdict counts what the COMPILE produced
 * instead, because those are not always the same number: a `when:` predicate
 * can gate nodes off, and "15 nodes to compile" followed by a 13-node compile
 * with no explanation is the page contradicting itself. So a gated compile
 * says both figures and says which one is which.
 */
function compileClause(verdict: CompileVerdict, nodeCount: number | undefined): string | undefined {
  const nodes = (n: number): string => `${n} node${n === 1 ? "" : "s"}`;
  switch (verdict.kind) {
    case "pending":
      return nodeCount === undefined ? "Compiling…" : `compiling ${nodes(nodeCount)}…`;
    case "compiled": {
      const compiled = `${nodes(verdict.nodeCount)} compile`;
      // Fan-out is why the number is a floor: those nodes each become one run
      // per element at dispatch, at a width no one can know before the
      // upstream node produces its iterable. Saying "15 nodes compile" and
      // then creating 60 runs would be the page's own figure misleading the
      // reader about the bill.
      const fanout =
        verdict.fanoutNodeCount > 0
          ? `${verdict.fanoutNodeCount} fan out, so runs exceed that`
          : undefined;
      const gated =
        verdict.gatedCount > 0 ? `${verdict.gatedCount} gated off by your parameters` : undefined;
      return [compiled, gated, fanout].filter((c) => c !== undefined).join(" · ");
    }
    case "failed":
      return `will not compile: ${verdict.message}`;
    case "idle":
      return nodeCount === undefined ? undefined : `${nodes(nodeCount)} to compile`;
  }
}
